import { View, ScrollView, TouchableOpacity, Animated } from 'react-native';
import { Text } from '@/components';
import { Stack, useRouter, useLocalSearchParams } from 'expo-router';
import { useUserProfile } from '@/lib/queries/account';
import { useAuth } from '@/context/AuthContext';
import { useDogs } from '@/context/DogsContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import styles from './summary.styles';
import { formatDuration } from '@/utils/distance';
import { ArrowLeft } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Haptics from 'expo-haptics';
import theme from '@/constants/colors';
import { loadEndWalkDraft, saveEndWalkDraft } from '@/utils/endWalkDraft';
import { logEvent } from '@/lib/analytics';

const RATING_LABELS = ['', 'notGreat', 'ok', 'good', 'great', 'lovedIt'];

export default function EndWalkSummary() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const params = useLocalSearchParams();
  const isFollowFlow = params.flow === 'follow';
  const draftId = typeof params.draftId === 'string' ? params.draftId : undefined;

  const [draft, setDraft] = useState<any>(undefined);

  useEffect(() => {
    if (!draftId) {
      // Fallback for any legacy URL that still carries the full draft param.
      try {
        if (typeof params.draft === 'string') {
          setDraft(JSON.parse(decodeURIComponent(params.draft)));
        }
      } catch (err) {
        console.warn('Failed to parse draft param in end-walk summary', err);
      }
      return;
    }
    let mounted = true;
    loadEndWalkDraft(draftId).then((loaded) => {
      if (mounted) setDraft(loaded ?? undefined);
    });
    return () => { mounted = false; };
  }, [draftId, params.draft]);

  const { session } = useAuth();
  const { data: userProfile } = useUserProfile(session?.accessToken);
  const { dogProfile } = useDogs();

  const displayUserName = userProfile?.name || (draft && draft.userName) || 'Username!';
  const { t } = useTranslation();
  const displayDogName = dogProfile?.name || (draft && draft.dogName) || 'DogName';

  const [hoveredStar, setHoveredStar] = useState(0);
  const starScales = useRef<Array<Animated.Value>>([
    new Animated.Value(1),
    new Animated.Value(1),
    new Animated.Value(1),
    new Animated.Value(1),
    new Animated.Value(1),
  ]).current;

  const animateStar = (index: number) => {
    const scale = starScales[index];
    Animated.sequence([
      Animated.spring(scale, { toValue: 1.35, useNativeDriver: true, speed: 40 }),
      Animated.spring(scale, { toValue: 1, useNativeDriver: true, speed: 30, bounciness: 12 }),
    ]).start();
  };

  const handleRatingPress = async (rating: number) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    animateStar(rating - 1);
    setHoveredStar(rating);
    logEvent('walk_rated', { rating, follow_flow: isFollowFlow });

    const enhanced = { ...(draft || {}), rating };
    const nextDraftId = await saveEndWalkDraft(enhanced, draftId);

    // Small delay so the user sees the feedback before navigating
    setTimeout(() => {
      if (isFollowFlow) {
        router.push(`/end-walk/follow-wrapup?draftId=${nextDraftId}`);
        return;
      }
      router.push(`/end-walk/review?draftId=${nextDraftId}&rating=${rating}`);
    }, 300);
  };

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <ScrollView contentContainerStyle={[styles.container, { paddingTop: insets.top + 20 }]}>
        <View style={styles.bigHeader}>
          <ArrowLeft size={20} color={styles.hugeTitle.color} onPress={() => router.push('/record')} />
          <Text style={styles.hugeTitle}>{t('walkSummary.greatWalk')}{draft && draft.name ? `\n${draft.name}!` : ` ${displayUserName}`} 👏</Text>
          <Text style={styles.bigSubtitle}>{t('walkSummary.enjoyedEveryStep', { dogName: displayDogName })}</Text>
        </View>

        {draft !== undefined && (
          <>
            <View style={styles.statsGridTop}>
              <View style={styles.statCard}><Text style={styles.statLabel}>{t('walkSummary.time')}</Text><Text style={styles.statValue}>{draft && draft.duration ? formatDuration(draft.duration) : '0s'}</Text></View>
              <View style={styles.statCard}><Text style={styles.statLabel}>{t('walkSummary.distance')}</Text><Text style={styles.statValue}>{draft && draft.distance ? `${Math.round(draft.distance)}m` : '0m'}</Text></View>
              <View style={styles.statCard}><Text style={styles.statLabel}>{t('walkSummary.elevation')}</Text><Text style={styles.statValue}>{draft && draft.maxElevation ? `${Math.round(draft.maxElevation)}m` : '0m'}</Text></View>
              <View style={styles.statCard}><Text style={styles.statLabel}>{t('walkSummary.sniffTime')}</Text><Text style={styles.statValue}>{draft && draft.sniffTime ? formatDuration(draft.sniffTime) : '0s'}</Text></View>
              <View style={styles.statCard}><Text style={styles.statLabel}>{t('walkSummary.pace')}</Text><Text style={styles.statValue}>{draft && draft.pace ? draft.pace : '0:00/km'}</Text></View>
              <View style={styles.statCard}><Text style={styles.statLabel}>{t('walkSummary.speed')}</Text><Text style={styles.statValue}>{draft && draft.speed ? `${(draft.speed).toFixed(1)}km/h` : '0.0km/h'}</Text></View>
            </View>

            <View style={{ height: 160 }} />
          </>
        )}

        <View style={{ height: 60 }} />
      </ScrollView>

      <View style={styles.bottomSheet} pointerEvents="box-none">
        <View style={styles.bottomContent}>
          <Text style={styles.bottomTitle}>{t('walkSummary.howWasTrail')}</Text>
          <Text style={styles.bottomSubtitle}>{t('walkSummary.tapAStar')}</Text>
          <View style={styles.starRow}>
            {[1, 2, 3, 4, 5].map((s) => {
              const filled = s <= hoveredStar;
              return (
                <Animated.View key={s} style={{ transform: [{ scale: starScales[s - 1] }] }}>
                  <TouchableOpacity
                    style={styles.starButton}
                    onPress={() => handleRatingPress(s)}
                    onPressIn={() => setHoveredStar(s)}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.star, filled && styles.starFilled]}>★</Text>
                  </TouchableOpacity>
                </Animated.View>
              );
            })}
          </View>
          {hoveredStar > 0 && (
            <View style={styles.ratingLabelContainer}>
              <Text style={styles.ratingLabel}>{t(`walkSummary.${RATING_LABELS[hoveredStar]}`)}</Text>
            </View>
          )}
        </View>
      </View>
    </>
  );
}
